<template>
  <div
    ref="containerRef"
    class="e-chart"
  />
</template>

<script lang="ts">
// Module-scope, one-time echarts setup (inside <script setup> these calls
// would re-run for every chart instance).
//
// Modular ECharts: register only the chart types / components / renderer the
// app uses. Importing from 'echarts' pulls the full ~800 KB bundle (every
// chart type, every component, every renderer). The set below is derived from
// EChartCombo/Bars/Pie/Prices.vue and the option keys they set.
import { registerTheme, use as useEcharts } from 'echarts/core'
import {
  BarChart,
  LineChart,
  PieChart,
  ScatterChart,
} from 'echarts/charts'
import {
  AxisPointerComponent,
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
} from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'

import lightTheme from './utils/themes/light'
import darkTheme from './utils/themes/dark'

useEcharts([
  BarChart, LineChart, PieChart, ScatterChart,
  AxisPointerComponent, DataZoomComponent, GridComponent,
  LegendComponent, TitleComponent, TooltipComponent,
  CanvasRenderer,
])

registerTheme('light', lightTheme)
registerTheme('dark', darkTheme)
</script>

<script setup lang="ts" generic="T extends EChartOption.Series">
import { nextTick, onUnmounted, shallowRef, useTemplateRef, watch } from 'vue'
import { useDark, useResizeObserver } from '@vueuse/core'

import {
  dispose as disposeEcharts,
  init as initEcharts,
} from 'echarts/core'
import type { EChartsType } from 'echarts/core'
import type { EChartOption } from 'echarts'

type Props = {
  option: EChartOption<T>
  selectedIndex?: number
}

type Emits = {
  'click:item': [index: number, name: string]
  'click:legend': [name: string, selected: Record<string, boolean>]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

defineExpose({
  getInstance() {
    return instance.value
  },
})

const isDark = useDark()

const containerRef = useTemplateRef('containerRef')

const instance = shallowRef<EChartsType>()

// Registered once in setup scope so vueuse can dispose it on unmount.
// (Calling it inside init() after an `await` leaves no active effect scope,
// which leaks one ResizeObserver per chart mount.)
useResizeObserver(containerRef, () => {
  instance.value?.resize()
})

const init = () => {
  if (!containerRef.value) return

  const theme = isDark.value ? 'dark' : 'light'
  const chart = initEcharts(containerRef.value, theme)

  chart.on('click', (data: any) => {
    emit('click:item', data.dataIndex, data.name)
  })

  chart.on('legendselectchanged', (data: any) => {
    emit('click:legend', data.name, data.selected)
  })

  if (props.selectedIndex !== undefined) {
    nextTick(() => {
      instance.value?.dispatchAction({
        type: 'select',
        dataIndex: props.selectedIndex,
      })
    })
  }

  return chart
}

const dispose = () => {
  if (!instance.value) return
  disposeEcharts(instance.value)
  instance.value = undefined
}

watch(
  () => props.option,
  async value => {
    if (!containerRef.value) {
      await nextTick()
    }
    if (!instance.value) {
      instance.value = init()
    } else {
      instance.value.clear()
    }
    instance.value?.setOption(value as Parameters<EChartsType['setOption']>[0])
  },
  // No `deep`: every consumer builds `option` via computed(), so a change
  // always produces a new object identity. Deep-traversing the full option
  // (data arrays included) on every flush is wasted work.
  { immediate: true },
)

watch(
  () => props.selectedIndex,
  (dataIndex, prevIndex) => {
    if (dataIndex === undefined) {
      if (prevIndex !== undefined) {
        instance.value?.dispatchAction({
          type: 'unselect',
          dataIndex: prevIndex,
        })
      }
      return
    }
    instance.value?.dispatchAction({ type: 'select', dataIndex })
  },
  { immediate: true },
)

watch(isDark, () => {
  dispose()
  instance.value = init()
  instance.value?.setOption(
    props.option as Parameters<EChartsType['setOption']>[0],
  )
})

// echarts keeps every instance in a module-level registry until dispose()
// is called, so skipping this leaks the canvas + data model on each unmount.
onUnmounted(dispose)
</script>

<style scoped>
.e-chart {
  @apply w-full min-h-55;
}
</style>
